import type { PhysicalAddress } from '@interfaces/physicalAddress';
import {
  Dialog,
  DialogTitle,
  List,
  ListItem,
  MenuItem,
  TextField,
} from '@mui/material';
import type { CountryCodes } from '@onevesthq/ov-enums';
import { provinces } from '@ovComponents/resources';
import { generateAddressString } from '@utils/common';
import { Button, Grid } from 'hephaestus/web';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

const AddressField = ({
  onChange,
  label,
  rows = 1,
  readonly = false,
  address,
  autoFocus = false,
  error = false,
}: {
  onChange: (e: PhysicalAddress) => void;
  label: string;
  rows?: number;
  readonly?: boolean;
  autoFocus?: boolean;
  address: PhysicalAddress;
  error?: boolean;
}) => {
  const { t } = useTranslation('components');
  const [open, setOpen] = useState(false);
  const [physicalAddress, setPhysicalAddress] = useState<PhysicalAddress>({
    city: address?.city || '',
    country: address?.country,
    houseNumber: address?.houseNumber || '',
    neighborhood: address?.neighborhood || '',
    postal: address?.postal || '',
    province: address?.province || '',
    streetName: address?.streetName || '',
    unitNumber: address?.unitNumber || '',
  });

  return (
    <>
      <TextField
        fullWidth
        autoFocus={autoFocus}
        data-testid='addressTextField'
        value={generateAddressString(physicalAddress)}
        onClick={() => setOpen(!open)}
        label={label}
        multiline={rows > 1}
        rows={rows > 1 ? rows : undefined}
        InputProps={{
          autoComplete: 'none',
          readOnly: readonly,
        }}
        error={error}
      />
      <Dialog onClose={() => setOpen(false)} open={open} fullWidth>
        <DialogTitle>{t('components:address.title')}</DialogTitle>
        <Grid
          container
          spacing={2}
          sx={{
            px: 2,
          }}
        >
          <Grid key={t('address.unitNumber') as string} size={6}>
            <TextField
              fullWidth
              value={physicalAddress.unitNumber}
              data-testid='unitNumberTextField'
              label={t('components:address.unitNumber')}
              onChange={(e) =>
                setPhysicalAddress((prevState) => ({
                  ...prevState,
                  unitNumber: e.target.value,
                }))
              }
              InputProps={{
                readOnly: readonly,
              }}
            />
          </Grid>
          <Grid key={t('components:address.houseNumber') as string} size={6}>
            <TextField
              fullWidth
              data-testid='houseNumberTextField'
              value={physicalAddress.houseNumber}
              label={t('components:address.houseNumber')}
              onChange={(e) =>
                setPhysicalAddress((prevState) => ({
                  ...prevState,
                  houseNumber: e.target.value,
                }))
              }
              InputProps={{
                readOnly: readonly,
              }}
            />
          </Grid>
          <Grid key={t('components:address.streetName') as string} size={12}>
            <TextField
              fullWidth
              data-testid='streetTextField'
              value={physicalAddress.streetName}
              label={t('components:address.streetName')}
              onChange={(e) =>
                setPhysicalAddress((prevState) => ({
                  ...prevState,
                  streetName: e.target.value,
                }))
              }
              InputProps={{
                readOnly: readonly,
              }}
            />
          </Grid>
          <Grid key={t('components:address.city') as string} size={6}>
            <TextField
              fullWidth
              data-testid='cityTextField'
              value={physicalAddress.city}
              label={t('components:address.city')}
              onChange={(e) =>
                setPhysicalAddress((prevState) => ({
                  ...prevState,
                  city: e.target.value,
                }))
              }
              InputProps={{
                readOnly: readonly,
              }}
            />
          </Grid>
          <Grid key={t('components:address.province') as string} size={6}>
            <TextField
              fullWidth
              select
              data-testid='provinceTextField'
              value={physicalAddress.province}
              label={t('components:address.province')}
              onChange={(e) =>
                setPhysicalAddress((prevState) => ({
                  ...prevState,
                  province: e.target.value,
                }))
              }
              InputProps={{
                readOnly: readonly,
              }}
            >
              {provinces.map((province: { key: string; value: string }) => (
                <MenuItem key={province.key} value={province.key}>
                  {province.value}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid key={t('components:address.postal') as string} size={6}>
            <TextField
              fullWidth
              data-testid='postalTextField'
              value={physicalAddress.postal}
              label={t('components:address.postal')}
              onChange={(e) =>
                setPhysicalAddress((prevState) => ({
                  ...prevState,
                  postal: e.target.value,
                }))
              }
              InputProps={{
                readOnly: readonly,
              }}
            />
          </Grid>
          <Grid key={t('components:address.country') as string} size={6}>
            <TextField
              fullWidth
              select
              data-testid='countryTextField'
              value={physicalAddress.country}
              label={t('components:address.country')}
              onChange={(e) =>
                setPhysicalAddress((prevState) => ({
                  ...prevState,
                  country: e.target.value as CountryCodes,
                }))
              }
              InputProps={{
                readOnly: readonly,
              }}
            >
              <MenuItem key='CA' value='CA'>
                Canada
              </MenuItem>
            </TextField>
          </Grid>
        </Grid>
        <List>
          <ListItem>
            <Button
              data-testid='confirmButton'
              fullWidth
              variant='contained'
              onClick={() => {
                setOpen(false);
                onChange(physicalAddress);
              }}
            >
              {t('done')}
            </Button>
          </ListItem>
        </List>
      </Dialog>
    </>
  );
};

export default AddressField;
